import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { Button, Dialog, TextField, Card, CardContent, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';

import { RootState, AppDispatch } from '../redux/store';
import { fetchCollections } from '../collections/redux/collectionsSlice';
import { DataTable } from '../shared/components/DataTable';
import { Section } from '../shared/components/Section';
import { GlowAlert } from '../shared/components/GlowAlert';

interface NewCollection {
  name: string;
  description: string;
}

const CollectionsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const collections = useSelector((state: RootState) => state.collections.collections);
  const loading = useSelector((state: RootState) => state.collections.loading);
  const error = useSelector((state: RootState) => state.collections.error);
  const [openDialog, setOpenDialog] = useState(false);
  const [formData, setFormData] = useState<NewCollection>({ name: '', description: '' });

  useEffect(() => {
    dispatch(fetchCollections());
  }, [dispatch]);

  const handleAddClick = () => {
    setFormData({ name: '', description: '' });
    setOpenDialog(true);
  };

  const handleSave = () => {
    // TODO: Add API endpoint to create collection
    setOpenDialog(false);
  };

  const handleDelete = (id: string) => {
    // TODO: Add API endpoint to delete collection
  };

  const columns = [
    { key: 'name', label: 'Name' },
    { key: 'count', label: 'Vectors' },
  ];

  return (
    <Section title="Collections" onRefresh={() => dispatch(fetchCollections())} refreshing={loading}>
      <Card sx={{ mb: 2.5, backgroundColor: 'background.paper' }}>
        <CardContent>
          <Typography variant="body2" color="textPrimary" sx={{ lineHeight: 1.6 }}>
            <strong>What is this?</strong> Collections are vector embeddings of your data—memories, conversations,
            and knowledge—organized and stored for semantic search and retrieval.
          </Typography>
          <Typography variant="body2" color="textSecondary" sx={{ lineHeight: 1.6, mt: 1 }}>
            <strong>Purpose:</strong> VictorIA uses these collections to find relevant context and memories
            when answering your questions, providing personalized and contextually aware responses.
          </Typography>
        </CardContent>
      </Card>

      {error && <GlowAlert sx={{ mb: 2.5 }}>{error}</GlowAlert>}
      {loading && <GlowAlert severity="info">Loading collections...</GlowAlert>}

      {!loading && (
        <>
          <div style={{ marginBottom: '20px' }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleAddClick}
            >
              New Collection
            </Button>
          </div>

          <DataTable
            columns={columns}
            data={collections}
            getRowId={(row) => row.id}
            onDelete={(row) => handleDelete(row.id)}
          />
        </>
      )}

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="sm" fullWidth>
        <div style={{ padding: '20px' }}>
          <h2>Create New Collection</h2>
          <TextField
            fullWidth
            label="Collection Name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            margin="normal"
          />
          <TextField
            fullWidth
            label="Description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            margin="normal"
            multiline
            rows={3}
          />
          <div style={{ marginTop: '20px', display: 'flex', gap: '10px' }}>
            <Button variant="contained" onClick={handleSave}>
              Create
            </Button>
            <Button variant="outlined" onClick={() => setOpenDialog(false)}>
              Cancel
            </Button>
          </div>
        </div>
      </Dialog>
    </Section>
  );
};

export default CollectionsPage;
